import type { IntegrationOptOutReason, OpenBankingConnectionFailure } from './Activity'

export interface Recommendation {
  id: string
  category?: string
  description: string
  completed: boolean
  createdAt?: string
  updatedAt?: string
}

export interface DataRequestRecommendation extends Recommendation {
  author: string
  displayName: string
  requestMessage: string
  status: StatusTypes | null
}

export interface UploadDocumentRecommendation extends Recommendation {
  requestMessage: string
  documentType: string
  author: string
  status: StatusTypes | null
}

export type StatusTypes = 'Active' | 'Complete' | 'Cancelled' | 'Expired' | 'Declined'

export enum RequestType {
  OPEN_BANKING = 'openBankingRequest',
  PAYROLL = 'payrollRequest',
}

/**
 * Trusted Source requests (Open Banking + Payroll) share the same data shape.
 * We model them as a discriminated union on `category` to get type narrowing
 * without relying on runtime class instances.
 */
export interface TrustedSourceRecommendationBase extends Recommendation {
  category: RequestType
  requestMessage: string
  author: string
  status: StatusTypes | null
  optOutReason?: IntegrationOptOutReason | null
  connectionFailures?: OpenBankingConnectionFailure[] | null
}

export type OpenBankingRecommendation = TrustedSourceRecommendationBase & {
  category: RequestType.OPEN_BANKING
}

export type PayrollRecommendation = TrustedSourceRecommendationBase & {
  category: RequestType.PAYROLL
}

export type TrustedSourceRecommendation =
  | OpenBankingRecommendation
  | PayrollRecommendation

export const isOpenBankingRecommendation = (
  recommendation: Recommendation,
): recommendation is OpenBankingRecommendation =>
  recommendation.category === RequestType.OPEN_BANKING

export const isPayrollRecommendation = (
  recommendation: Recommendation,
): recommendation is PayrollRecommendation =>
  recommendation.category === RequestType.PAYROLL
